// Strings for the chunk-load fallback, with English defaults. Each app root
// registers i18next's translator once i18n is initialized. Importing i18next
// here would put its runtime in the entry chunk of every app. Like
// i18nLocaleLoader, this module must stay free of i18next imports.
type ChunkErrorTranslate = (key: string, defaultValue: string) => string;

let translate: ChunkErrorTranslate = (_key, defaultValue) => defaultValue;

export const registerChunkErrorTranslator = (
  next: ChunkErrorTranslate
): void => {
  translate = next;
};

export const chunkErrorMessage = (): string =>
  translate(
    'error.chunkLoad.message',
    'Something went wrong while loading the page.'
  );

export const chunkErrorReloadLabel = (): string =>
  translate('error.chunkLoad.reloadButton', 'Reload');

// The app-entry failure renders before any app chunk or i18next loads, so its
// one sentence keeps its own small table, keyed by base language code.
const APP_LOAD_FAILURE_ENGLISH =
  'Something went wrong while loading the page. Please refresh to try again.';
const APP_LOAD_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  de: 'Beim Laden der Seite ist etwas schiefgelaufen. Bitte laden Sie die Seite neu, um es erneut zu versuchen.',
  es: 'Se produjo un error al cargar la página. Actualice la página para volver a intentarlo.',
  fr: 'Une erreur s’est produite lors du chargement de la page. Veuillez actualiser la page pour réessayer.',
  hi: 'पेज लोड करते समय कुछ गलत हो गया। कृपया फिर से कोशिश करने के लिए पेज रीफ़्रेश करें।',
  it: 'Si è verificato un errore durante il caricamento della pagina. Aggiorni la pagina per riprovare.',
  ja: 'ページの読み込み中に問題が発生しました。ページを更新して、もう一度お試しください。',
  nl: 'Er is iets misgegaan bij het laden van de pagina. Vernieuw de pagina om het opnieuw te proberen.',
  pt: 'Ocorreu um erro ao carregar a página. Atualize a página para tentar novamente.',
  zh: '加载页面时出错。请刷新页面后重试。',
};

/** The app-entry failure message in `language` (a locale code), else English. */
export const appLoadFailureMessage = (
  language: string | null | undefined
): string => {
  const base = language?.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return (
    (Object.hasOwn(APP_LOAD_FAILURE_MESSAGES, base)
      ? APP_LOAD_FAILURE_MESSAGES[base]
      : undefined) ?? APP_LOAD_FAILURE_ENGLISH
  );
};
